// Tremor Date Picker [v2.0.0]

"use client"

import { forwardRef, type ComponentProps } from "react"
import * as PopoverPrimitives from "@radix-ui/react-popover"
import { tv, type VariantProps } from "tailwind-variants"

import { cx, hasErrorInput } from "@/lib/utils"

import { UpDownArrowsIcon } from "@/components/icons"

const triggerStyles = tv({
    base: [
        // base
        "peer flex min-w-0 w-auto cursor-pointer appearance-none items-center gap-x-2 border-b-2 px-0 py-2 outline-hidden transition-all sm:text-sm font-roboto",
        // background color
        "bg-transparent",
        // text color
        "text-gray-900",
        // placeholder color
        "placeholder-gray-400",
        // hover
        "hover:brightness-90",
        // disabled
        "disabled:pointer-events-none",
        "disabled:bg-transparent disabled:text-gray-400",
        // focus
        "focus:outline-none",
    ],
    variants: {
        hasError: {
            true: hasErrorInput,
        },
        isCustom: {
            true: "py-0 border-b-0",
        },
    },
})

interface TriggerProps
    extends ComponentProps<"button">,
    VariantProps<typeof triggerStyles> {
    placeholder?: string
    compareLabel?: string
    isCustom?: boolean
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
    (
        { className, children, placeholder, compareLabel, hasError, isCustom, ...props }: TriggerProps,
        forwardedRef,
    ) => {
        return (
            <PopoverPrimitives.Trigger asChild>
                <button
                    ref={forwardedRef}
                    className={cx(triggerStyles({ hasError, isCustom }), className)}
                    style={{ borderColor: isCustom ? 'transparent' : '#6d7d8a' }}
                    {...props}
                >
                    <span className="flex-1 overflow-hidden text-left text-ellipsis whitespace-nowrap" style={{ color: '#232F34' }}>
                        {children ? (
                            children
                        ) : placeholder ? (
                            <span className="text-gray-400">
                                {placeholder}
                            </span>
                        ) : null}
                    </span>
                    {compareLabel ? (
                        <span
                            className="inline-flex h-5 max-w-[240px] shrink-0 items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-full border px-2 text-xs font-medium leading-none"
                            style={{ borderColor: '#2f6f73', backgroundColor: '#f5fafa', color: '#2f6f73' }}
                            title={compareLabel}
                        >
                            {compareLabel}
                        </span>
                    ) : null}
                    {!isCustom && <UpDownArrowsIcon className="block shrink-0" />}
                </button>
            </PopoverPrimitives.Trigger>
        )
    },
)

Trigger.displayName = "DatePicker.Trigger"

export { Trigger }